import { lazy, Suspense } from 'react';
import type { MonacoPaneProps } from './MonacoPane.types';

export type { MonacoPaneAction, MonacoMarker, MonacoPaneProps } from './MonacoPane.types';
export { inferDownloadFilename, languageToExtension } from './MonacoPane.types';

/**
 * Lazy outer wrapper around the actual Monaco-importing component.
 *
 * Keeping this thin shell ensures that:
 *   - non-tool routes never download the ~3 MB monaco vendor chunk;
 *   - editable / read-only panes share one Suspense boundary;
 *   - swapping the editor backend (e.g. CodeMirror as a fallback)
 *     only touches MonacoPaneInner, not every call site.
 *
 * Vite splits the import boundary; the chunk landing here pulls
 * `monaco-editor` + `@monaco-editor/react` together (see
 * `vite.config.ts manualChunks` for the consolidation rule).
 */
const Inner = lazy(() => import('./MonacoPaneInner'));

export function MonacoPane(props: MonacoPaneProps): JSX.Element {
  return (
    <Suspense fallback={<MonacoPaneSkeleton title={props.title} minHeight={props.minHeight} />}>
      <Inner {...props} />
    </Suspense>
  );
}

function MonacoPaneSkeleton({
  title,
  minHeight = 240,
}: {
  title: string;
  minHeight?: number;
}): JSX.Element {
  return (
    <section
      className="flex min-h-0 flex-col rounded-md border border-line bg-bg-card"
      style={{ minHeight }}
    >
      <header className="flex items-center justify-between border-b border-line/60 px-2 py-1">
        <h3 className="text-[11px] font-medium uppercase tracking-wide text-ink-dim">{title}</h3>
      </header>
      <div className="flex flex-1 items-center justify-center text-[11px] text-ink-dim">
        加载编辑器中…
      </div>
    </section>
  );
}
